import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";

ChartJS.register(ArcElement, Tooltip, Legend);

const NipponSipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
      question: "What is the minimum SIP amount I can start with?",
      answer:
        "Most mutual funds, including Nippon India, allow you to start SIPs with as low as ₹500 per month.",
    },
    {
      question: "Can I modify my SIP amount?",
      answer:
        "Yes, you can increase or decrease the SIP amount based on your financial goals and changing income levels.",
    },
    {
      question: "How accurate are the projections from the SIP calculator?",
      answer:
        "The projections provided by the SIP calculator are based on estimates and assumptions. The actual returns may vary depending on market performance.",
    },
    {
      question: "Is SIP risk-free?",
      answer:
        "SIPs involve investing in mutual funds, so they come with market risks, especially in equity funds.",
    },
    {
      question: "Can I pause or stop my SIP?",
      answer:
        "Yes, you can pause or stop your SIP at any time.",
    },
    {
      question: "Can I use the SIP calculator for tax-saving investments?",
      answer:
        "Yes, the calculator can be used for tax-saving SIPs in funds like ELSS (Equity Linked Savings Scheme).",
    },
    {
      question: "Can SIP returns be taxed?",
      answer:
        "Yes, SIP returns are subject to capital gains tax, depending on the type of fund and the holding period.",
    },
    {
      question: "How do I select the right mutual fund for SIP?",
      answer:
        "Choose mutual funds based on your risk tolerance, investment goals, and time horizon. Equity funds are riskier but provide higher returns, while debt funds are safer but offer lower returns.",
    },
    {
      question: "Can I invest in multiple SIPs at once?",
      answer:
        "Yes, you can start multiple SIPs in different funds depending on your risk appetite and investment goals.",
    },
    {
      question: "What happens if I miss a SIP payment?",
      answer:
        "Missing a payment will not affect your SIP, but it will be treated as a skipped month, and your investment will resume in the following month.",
    },
    {
      question: "What is the lock-in period for SIP investments?",
      answer:
        "For tax-saving mutual funds (ELSS), there is typically a 3-year lock-in period, while other funds have no lock-in.",
    },
    {
      question: "Can I redeem my SIP before the maturity period?",
      answer:
        "Yes, you can redeem your SIP units at any time, but the returns might be lower if you exit prematurely.",
    },
    {
      question: "What if my returns are not as expected?",
      answer:
        "SIPs are long-term investments. The returns fluctuate based on market conditions, but they tend to even out over the long run.",
    },
    {
      question: "Can I change the frequency of my SIP?",
      answer:
        "Yes, you can change your SIP frequency from monthly to quarterly or vice versa based on your preferences.",
    },
    {
      question: "Are SIPs suitable for beginners?",
      answer:
        "Yes, SIPs are perfect for beginners because they allow small, regular investments without requiring market expertise.",
    }
  ]

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
        <title>
          Nippon Bank SIP Calculator- Calculate Returns on Nippon Bank SIP Investments |
          TheFinmaster
        </title>
        {/* <meta
          name="description"
          content="Free SIP Calculator! Estimate your returns on Mutual Fund investments with Systematic Investment Plans."
        />
        <meta
          name="keywords"
          content="sip return rate, monthly sip calculator, step up sip, sip calculator online"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/sip-calculator"
        /> */}
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>

      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> SBI SIP Calculator</span>
          </li>
        </ol>
      </nav>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
          Nippon Bank SIP Returns Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is the Nippon India SIP Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The Nippon India SIP Calculator is a free online tool designed to help investors estimate the future value of their investments in mutual funds through a Systematic Investment Plan (SIP). By using this tool, you can input different investment parameters such as the monthly SIP amount, expected rate of return, and investment duration to estimate the potential returns you could earn. This calculator is especially useful for those who wish to plan their investments for long-term goals like retirement, purchasing a home, or funding their children’s education.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The Nippon India SIP Calculator makes the process of evaluating SIP investments much simpler and more transparent. It gives investors an idea of how their money can grow over time, helping them make better-informed decisions. The tool is designed to allow users to adjust key parameters and visualize how these changes can impact their investment's growth, making it an essential tool for effective financial planning.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why is it Popular?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Systematic Investment Plan (SIP) is a method of investing a fixed amount regularly in mutual funds. It allows investors to buy units of a mutual fund at prevailing market prices, regardless of market conditions. Each SIP payment is invested in the fund, and over time, these investments grow, benefiting from compounding and dollar-cost averaging.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Why is SIP Popular?
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Disciplined Approach:</strong> SIPs encourage regular savings and investments. The process of investing small amounts regularly builds a disciplined investing habit, which is ideal for long-term wealth creation.</li>
              <li><strong>Flexibility:</strong> SIPs provide great flexibility in terms of the amount invested, frequency, and mutual fund choice. You can start with as little as ₹500 per month and choose any mutual fund that aligns with your financial goals.</li>
              <li><strong>Dollar-Cost Averaging:</strong> SIPs help mitigate the risk of market volatility by spreading the investment across different market cycles. Since SIP invests consistently, regardless of market ups and downs, it helps you buy more units when the market is low and fewer units when the market is high, averaging out the cost of your investment.</li>
              <li><strong>Power of Compounding:</strong> SIP investments, especially in equity mutual funds, benefit from the power of compounding. The returns generated from the investment are reinvested, and those returns begin to earn additional returns, which accelerates growth over time.</li>
              <li><strong>Accessibility:</strong> SIPs are accessible to everyone, regardless of their income level, making them a preferred choice for investors across all economic backgrounds.</li>
              <li><strong>Long-term Financial Goals:</strong> SIPs are ideally suited for long-term financial goals such as retirement planning, building a corpus for children’s education, or purchasing a property.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can the Nippon India SIP Calculator Help You?
            </h1>
            <p className= "mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Nippon India SIP Calculator is a powerful tool that assists investors in several ways:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Estimate Future Value:</strong> By entering the SIP amount, expected return rate, and investment tenure, the calculator estimates the future value of your SIP investment, giving you an idea of how much your corpus will grow over time.</li>
              <li><strong>Informed Decision-Making:</strong> The calculator helps you determine whether you need to increase your SIP amount or extend your investment duration to achieve your financial goals. By comparing different scenarios, it helps you make informed investment decisions.</li>
              <li><strong>Visual Representation:</strong> The tool typically presents results in the form of graphs or tables, making it easier to visualize how your investment grows over time. This visual representation can inspire confidence and help keep you motivated to stick with your SIP strategy.</li>
              <li><strong>Real-Time Estimates:</strong> It provides quick results based on real-time calculations, allowing you to adjust the investment parameters as you go along and get immediate feedback on how these changes affect your returns.</li>
              <li><strong>Easy to Use:</strong> The Nippon India SIP Calculator is intuitive and straightforward. You don’t need advanced financial knowledge to use it. It provides results in a user-friendly format, making financial planning more accessible for everyone.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Nippon India Mutual Fund SIP Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Nippon India Mutual Fund SIP Calculator works by applying a standard compound interest formula to calculate the potential future value of your SIP investment. Here’s how it works:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Inputs Required:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Monthly SIP Amount:</strong> The fixed amount of money you plan to invest regularly.</li>
              <li><strong>Expected Rate of Return:</strong> The annualized return you expect from your mutual fund. Typically, this can vary based on the type of fund chosen, such as equity or debt funds.</li>
              <li><strong>Investment Duration:</strong> The time period for which you will continue investing (in years or months).</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula for Calculation:</strong><br />
              The future value of your SIP is calculated using the formula for compound interest, adjusted for monthly contributions:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A = P × [(1 + r)^n – 1] / r
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>A is the future value of the SIP (corpus value).</li>
              <li>P is the monthly SIP contribution.</li>
              <li>r is the monthly rate of return (annual rate of return divided by 12).</li>
              <li>n is the total number of months (investment duration in years multiplied by 12).</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Let’s say you invest ₹5,000 every month for 10 years, with an expected annual return of 12%. The calculator will use the formula to calculate how much your ₹5,000 SIP will grow in 10 years, considering compound growth over that time.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              In addition, the calculator may account for other factors such as periodic compounding (quarterly or annually) and reinvestment of dividends, providing a more accurate estimate of your future corpus.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the Nippon India SIP Calculator Online
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the Nippon India SIP Calculator is a quick and simple process:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Visit the Nippon India SIP Calculator Page: Go to the official Nippon India Mutual Fund website, where the SIP calculator is hosted.</li>
              <li>Enter Your Monthly SIP Amount: Input the amount you plan to invest each month. This is typically the fixed amount you want to contribute to the SIP.</li>
              <li>Enter the Expected Rate of Return: Choose or enter the expected annual return rate for your mutual fund investment. This can be based on historical returns or estimates provided by the fund.</li>
              <li>Select Your Investment Duration: Choose how long you plan to invest. This could range from a few years to several decades, depending on your financial goal.</li>
              <li>Click on ‘Calculate’: After entering all the details, click on the calculate button to generate the results.</li>
              <li>View the Results: The calculator will provide you with an estimate of the future value of your SIP corpus. This will include your total investment, the return amount, and the final value at the end of your investment period.</li>
              <li>Adjust and Compare: You can change the parameters such as SIP amount, return rate, or duration to see how different scenarios affect your returns.</li>
            </ol>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Advantages of Using TheFinmaster Nippon India SIP Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The TheFinmaster Nippon India SIP Calculator offers several key advantages that make it an invaluable tool for both novice and seasoned investors:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Quick and Accurate Results:</strong> With the Nippon India SIP Calculator from TheFinmaster, you can instantly generate accurate projections of your potential returns. This allows you to understand the future value of your SIP investment without delay.</li>
              <li><strong>Customizable Investment Plans:</strong> The calculator allows you to input your own parameters such as SIP amount, expected rate of return, and investment duration, making it easy to tailor your investment plans according to your goals.</li>
              <li><strong>Goal-Oriented Projections:</strong> By using the calculator, you can estimate the corpus required to meet specific financial goals like buying a house, funding education, or preparing for retirement. This helps you align your SIP investment to your financial needs.</li>
              <li><strong>User-Friendly Interface:</strong> TheFinmaster’s SIP calculator is designed with a simple, intuitive interface that doesn’t require any advanced financial knowledge. Just input a few key details, and the tool does the rest, providing you with easy-to-understand results.</li>
              <li><strong>Real-Time Visualization:</strong> The results are often displayed in easy-to-read charts or graphs, helping you visualize how your SIP investment grows over time. This is an excellent way to track your investment journey and stay motivated.</li>
              <li><strong>No Cost, No Commitment:</strong> The tool is entirely free to use, allowing you to experiment with different scenarios and projections without any financial commitment. Whether you’re just exploring your options or fine-tuning an investment plan, it’s accessible to everyone.</li>
              <li><strong>Transparency in Investment Growth:</strong> By breaking down the growth of your SIP and showing how compounding works, the calculator provides a transparent view of how your money will grow over time. This transparency helps in making informed decisions about your investment.</li>
              <li><strong>Track Progress Over Time:</strong> As you invest over time, the calculator can help you track your progress and compare how different amounts or duration affect the final corpus, enabling you to adjust your strategy as needed.</li>
              <li><strong>Comprehensive Financial Planning:</strong> The calculator not only helps in SIP-specific calculations but also helps you plan for larger financial goals by adjusting factors like contribution amount and returns to simulate different scenarios.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Which Factors Influence SIP Earnings?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Several key factors can affect the returns from your SIP investments:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Rate of Return:</strong> The rate of return is one of the most significant factors influencing your earnings. Equity funds, for example, tend to have higher returns but also come with more risk, while debt funds generally provide lower but more stable returns.</li>
              <li><strong>SIP Amount:</strong> The higher your monthly SIP contribution, the greater the potential returns. A consistent and higher contribution over a long period will lead to a larger corpus.</li>
              <li><strong>Investment Duration:</strong> The longer the duration of your SIP, the more time your investments have to benefit from the power of compounding. A longer investment horizon typically leads to higher returns.</li>
              <li><strong>Market Conditions:</strong> While SIPs help to mitigate short-term market fluctuations, the overall market performance still influences the returns over a long period.</li>
              <li><strong>Compounding Frequency:</strong> The frequency with which returns are compounded (monthly, quarterly, etc.) can impact the growth of your investment. More frequent compounding leads to better returns.</li>
              <li><strong>Reinvestment of Dividends:</strong> If the mutual fund distributes dividends, reinvesting those dividends can further boost your SIP’s growth.</li>
            </ul>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
        <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default NipponSipCalculator;
